import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { NOVA_DEMO } from "../lib/demoData";
import { buildDemoData } from "../lib/industries";

const AppCtx = createContext(null);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking done->false or object
  const [checking, setChecking] = useState(true);
  const [demo, setDemo] = useState(() => sessionStorage.getItem("lb_demo") === "1");
  const [data, setData] = useState(() => {
    const d = sessionStorage.getItem("lb_data");
    return d ? JSON.parse(d) : null;
  });

  // persist demo session so a refresh doesn't kick the visitor out
  useEffect(() => {
    if (demo) {
      sessionStorage.setItem("lb_demo", "1");
      if (data) sessionStorage.setItem("lb_data", JSON.stringify(data));
    } else {
      sessionStorage.removeItem("lb_demo");
      sessionStorage.removeItem("lb_data");
    }
  }, [demo, data]);
  const [pendingContent, setPendingContent] = useState(null); // hand-off to Content Studio
  const [pendingCommand, setPendingCommand] = useState(null); // hand-off to Command Center (auto-send)

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes("session_id=")) { setChecking(false); return; }
    try {
      const { data: u } = await api.get("/auth/me");
      setUser(u);
      if (u.profile) setData(u.profile);
    } catch {
      setUser(false);
    } finally { setChecking(false); }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Persist workspace server-side (debounced) for logged-in, non-demo accounts.
  const saveTimer = useRef();
  const hydrated = useRef(false);
  useEffect(() => {
    if (!user || demo || !data) return;
    if (!hydrated.current) { hydrated.current = true; return; } // skip first (freshly loaded) snapshot
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put("/state", { state: data }).catch((e) => console.error("Auto-save (/state) failed:", e));
    }, 700);
    return () => clearTimeout(saveTimer.current);
  }, [data, user, demo]);

  const startDemo = (profileId) => {
    setDemo(true);
    const lang = (typeof localStorage !== "undefined" && localStorage.getItem("lb_lang")) || "fr";
    setData(buildDemoData(profileId || "cafe", lang));
    // Jury demo: do NOT auto-launch the guided tour — the visitor should reach the
    // product value fast. The tour stays available via "Bien démarrer" (Help center).
  };

  // In demo, re-localize the personalized demo data when the language changes.
  useEffect(() => {
    const onLang = (e) => {
      setData((d) => (demo && d?.demoProfileId ? buildDemoData(d.demoProfileId, e.detail || "fr") : d));
    };
    window.addEventListener("lb-lang", onLang);
    return () => window.removeEventListener("lb-lang", onLang);
  }, [demo]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.error("Logout request failed:", e); }
    setUser(false); setData(null); setDemo(false);
  };

  const hashObjective = (s) =>
    (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim().slice(0, 60);

  // Compute plan progress over the scope (missions if any, else all actions).
  const progressOf = (actions) => {
    const scope = actions.filter((a) => a.isMission);
    const s = scope.length ? scope : actions;
    const done = s.filter((a) => a.done).length;
    return { done, total: s.length, pct: s.length ? Math.round((done / s.length) * 100) : 0 };
  };

  const pushRecent = (cur, text) =>
    [{ text, time: "à l'instant", type: "mission" }, ...(cur.recent || [])].slice(0, 8);

  // local action toggle (works for demo + logged)
  const toggleAction = (id) => {
    setData((d) => {
      if (!d) return d;
      const actions = (d.actions || []).map((a) => {
        if (a.id !== id) return a;
        const done = !a.done;
        return { ...a, done, status: done ? "done" : "todo" };
      });
      const target = actions.find((a) => a.id === id);
      let recent = d.recent;
      if (target && target.done) {
        const p = progressOf(actions);
        recent = pushRecent(d, `Mission terminée : ${target.title} · Progression ${p.pct}%`);
      }
      return { ...d, actions, recent };
    });
  };

  // set a mission status: "todo" | "progress" | "done"
  const setActionStatus = (id, status) => {
    setData((d) => {
      if (!d) return d;
      const prev = (d.actions || []).find((a) => a.id === id);
      const prevStatus = prev ? (prev.status || (prev.done ? "done" : "todo")) : "todo";
      const actions = (d.actions || []).map((a) =>
        a.id === id ? { ...a, status, done: status === "done" } : a);
      const target = actions.find((a) => a.id === id);
      let recent = d.recent;
      // Only log when the status actually changes (no duplicate events on re-apply).
      if (target && status !== prevStatus) {
        if (status === "done") {
          const p = progressOf(actions);
          recent = pushRecent(d, `Mission terminée : ${target.title} · Progression ${p.pct}%`);
        } else if (status === "progress") {
          recent = pushRecent(d, `Mission en cours : ${target.title}`);
        }
      }
      return { ...d, actions, recent };
    });
  };

  const addCalendarEvent = (evt) => {
    setData((d) => ({ ...d, calendar: [...(d?.calendar || []), { id: `e${Date.now()}`, ...evt }] }));
  };
  const removeCalendarEvent = (id) => {
    setData((d) => ({ ...d, calendar: (d?.calendar || []).filter((e) => e.id !== id) }));
  };

  // Turn an AI plan into real operational missions synced across Dashboard/Calendar/Content.
  // Dedupe on `key` (the deterministic user message), never on the LLM objective which drifts.
  // Returns { ok, duplicate }.
  const launchPlan = ({ objective, missions, key }) => {
    const hash = hashObjective(key || objective);
    let result = { ok: false, duplicate: false };
    setData((d) => {
      const cur = d || {};
      const launched = cur.launchedPlans || [];
      if (launched.includes(hash)) { result = { ok: false, duplicate: true }; return cur; }
      const today = new Date().getDate();
      const missionActions = missions.map((m) => ({
        ...m,
        done: false,
        status: "todo",
        isMission: true,
        planId: `plan_${hash}`,
      }));
      const calEvents = missionActions
        .filter((m) => typeof m.day_offset === "number")
        .map((m) => ({
          id: `mc_${m.id}`,
          day: Math.min(30, Math.max(1, today + (m.day_offset || 0))),
          type: m.type || "tache",
          title: m.title,
          channel: m.channel,
          missionId: m.id,
        }));
      const recentEntries = [
        { text: `${missionActions.length} missions créées`, time: "à l'instant", type: "mission" },
        { text: `Plan créé : ${objective}`, time: "à l'instant", type: "mission" },
      ];
      result = { ok: true, duplicate: false };
      return {
        ...cur,
        plan: { id: `plan_${Date.now()}`, hash, objective, createdAt: Date.now(), missionIds: missionActions.map((x) => x.id) },
        launchedPlans: [...launched, hash],
        actions: [...missionActions, ...(cur.actions || [])],
        calendar: [...calEvents, ...(cur.calendar || [])],
        recent: [...recentEntries, ...(cur.recent || [])].slice(0, 8),
      };
    });
    return result;
  };

  return (
    <AppCtx.Provider value={{
      user, setUser, checking, demo, setDemo, startDemo, data, setData,
      logout, toggleAction, setActionStatus, addCalendarEvent, removeCalendarEvent, checkAuth,
      launchPlan, pendingContent, setPendingContent,
      pendingCommand, setPendingCommand,
    }}>
      {children}
    </AppCtx.Provider>
  );
}

export const useApp = () => useContext(AppCtx);
